import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useServerView, activeFilters, ListActions, ListPrint, clamp2 } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { PSummary } from "@/components/print/PrintDoc";
import { pl } from "@/components/quality/QrTools";
import { toast } from "sonner";
import { OutcomePicker, OutcomeBadge } from "@/components/le/OutcomePicker";
import { GoalCard } from "@/components/le/ObjectCards";
import api, { formatApiError } from "@/lib/api";
import { ArchiveButton } from "@/components/ArchiveDialog";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { Target, Loader2, Plus, Search, MessageSquare, ChevronRight, ListChecks, Download } from "lucide-react";

export const GOAL_ST_BADGE = {
  "Åben": "bg-[#F5F4F2] text-stone-600",
  "I gang": "bg-[#F4EFEC] text-[#806B61]",
  "Lukket": "bg-[#E7EADF] text-[#5f6b4a]",
};
export const GOAL_STATUSES = ["Åben", "I gang", "Lukket"];
const FILTERS = [["open", "Aktive"], ["overdue", "Overskredne"], ["closed", "Lukkede"], ["all", "Alle"]];

function Card({ children, className = "" }) { return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`}>{children}</div>; }
function Stat({ label, value, tone }) { return <Card className="p-4"><p className="text-xs text-stone-500">{label}</p><p className={`text-2xl font-semibold mt-0.5 ${tone}`}>{value}</p></Card>; }

function Field({ label, children }) { return <div><label className="text-xs font-medium text-stone-600 mb-1 block">{label}</label>{children}</div>; }

export function GoalFormDialog({ open, onOpenChange, users, goal, categories = [], onSaved }) {
  const editing = !!goal;
  const [title, setTitle] = useState(goal?.title || "");
  const [category, setCategory] = useState(goal?.category || "");
  const [respId, setRespId] = useState(goal?.responsible_id || null);
  const [coIds, setCoIds] = useState(goal?.co_responsible_ids || []);
  const [baseline, setBaseline] = useState(goal?.baseline || "");
  const [actionPlan, setActionPlan] = useState(goal?.action_plan || "");
  const [startDate, setStartDate] = useState(goal?.start_date || null);
  const [deadline, setDeadline] = useState(goal?.deadline || null);
  const [status, setStatus] = useState(goal?.status || "Åben");
  const [outcome, setOutcome] = useState(goal?.outcome || null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error("Angiv et mål"); return; }
    setSaving(true);
    try {
      const payload = { title, category, responsible_id: respId, co_responsible_ids: coIds, baseline, action_plan: actionPlan, start_date: startDate, deadline, status , outcome: status === "Lukket" ? outcome : undefined };
      const r = editing ? await api.patch(`/le/goals/${goal.id}`, payload) : await api.post("/le/goals", payload);
      toast.success(editing ? "Mål opdateret" : "Mål oprettet");
      onSaved?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="le-goal-form">
        <DialogHeader><DialogTitle>{editing ? "Redigér mål" : "Nyt kvalitets- / miljømål"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Mål"><Textarea data-testid="le-goal-title" value={title} onChange={(e) => setTitle(e.target.value)} rows={2} className="rounded-xl" placeholder="Fx: Reducér energiforbrug med 10 %" /></Field>
          <Field label="Kategori">
            <Input data-testid="le-goal-category" list="le-goal-cats" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-xl h-10" placeholder="Fx Miljø, Kvalitet, Energi…" />
            <datalist id="le-goal-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ansvarlig"><UserSingleSelect users={users} value={respId} onChange={setRespId} testid="le-goal-resp" className="h-10" placeholder="Vælg ansvarlig" /></Field>
            <Field label="Status">
              <div className="inline-flex rounded-xl border border-[#EBE8E3] p-1 flex-wrap">
                {GOAL_STATUSES.map((s) => (
                  <button key={s} type="button" data-testid={`le-goal-status-${s}`} onClick={() => setStatus(s)} className={`px-2.5 py-1.5 rounded-lg text-xs ${status === s ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{s}</button>
                ))}
              </div>
              {status === "Lukket" && goal?.status !== "Lukket" && <div className="mt-2"><OutcomePicker value={outcome} onChange={setOutcome} testid="le-goal-form-outcome" /></div>}
            </Field>
          </div>
          <Field label="Medansvarlige"><ResponsibleMultiSelect users={users} value={coIds} onChange={setCoIds} testid="le-goal-co" className="h-10" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Startdato"><DanishDatePicker value={startDate} onChange={setStartDate} testid="le-goal-start" allowClosed /></Field>
            <Field label="Frist (sluttidspunkt)"><DanishDatePicker value={deadline} onChange={setDeadline} testid="le-goal-deadline" allowClosed /></Field>
          </div>
          <Field label="Udgangspunkt (baseline)"><Textarea data-testid="le-goal-baseline" value={baseline} onChange={(e) => setBaseline(e.target.value)} rows={2} className="rounded-xl" placeholder="Hvor står vi i dag? Nøgletal / status ved start." /></Field>
          <Field label="Handlingsplan (uddybende)"><Textarea data-testid="le-goal-plan" value={actionPlan} onChange={(e) => setActionPlan(e.target.value)} rows={3} className="rounded-xl" placeholder="Hvordan når vi målet?" /></Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Annullér</Button>
          <Button data-testid="le-goal-save" onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{saving ? "Gemmer…" : "Gem"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GoalRow({ g, nav }) {
  const evalN = g.evaluation_count || 0;
  return (
    <tr data-testid={`le-goal-row-${g.id}`} onClick={() => nav(`/maal/${g.id}`)} className="border-t border-[#F0EDE8] hover:bg-[#FBFAF8] cursor-pointer">
      <td className="px-4 py-3 align-top whitespace-nowrap"><span className="font-mono text-xs text-stone-500">{g.goal_no}</span></td>
      <td className="px-4 py-3 align-top max-w-[420px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[#1A1919]">{g.title}</span>
          {g.overdue && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white text-[#A83232] border border-[#E9C9C3]">Overskredet</span>}
        </div>
        {(evalN > 0 || g.open_action_count > 0) && <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-stone-400">
          {evalN > 0 && <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{evalN} evaluering{evalN === 1 ? "" : "er"}</span>}
          {g.open_action_count > 0 && <span className="inline-flex items-center gap-1 text-[#806B61]"><ListChecks className="h-3 w-3" />{g.open_action_count} åben handling{g.open_action_count === 1 ? "" : "er"}</span>}
        </div>}
      </td>
      <td className="px-4 py-3 align-top text-stone-600">{g.responsible_name || "—"}</td>
      <td className={`px-4 py-3 align-top whitespace-nowrap ${g.overdue ? "text-[#A83232] font-medium" : "text-stone-600"}`}>{g.deadline ? daDate(g.deadline) : "—"}</td>
      <td className="px-4 py-3 align-top"><span className={`text-[11px] px-2 py-0.5 rounded-full ${GOAL_ST_BADGE[g.status] || ""}`}>{g.status}</span> <OutcomeBadge goal={g} /></td>
    </tr>
  );
}

export default function LeGoals() {
  const nav = useNavigate();
  const sv = useServerView("/le-view/goals", { defaults: { visning: "open" }, exportPath: "/le-view/goals/export.xlsx" });
  const [users, setUsers] = useState([]);
  const [full, setFull] = useState({});
  const [acts, setActs] = useState([]);
  useEffect(() => {
    api.get("/le/goals", { params: { view: "all" } }).then((r) => setFull(Object.fromEntries(r.data.map((g) => [g.id, g])))).catch(() => {});
    api.get("/le/actions", { params: { view: "all" } }).then((r) => setActs(r.data)).catch(() => {});
  }, [sv.rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const [createOpen, setCreateOpen] = useState(false);
  const goals = sv.rows;
  const loading = sv.data === null;
  const load = () => { sv.reload(); api.get("/users").then((us) => setUsers(us.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); };
  useEffect(() => { api.get("/users").then((us) => setUsers(us.data)).catch(() => {}); }, []);
  const visning = sv.getArr("visning");
  const setView = (v) => sv.setParam({ visning: v === "all" ? [] : [v] });
  const curView = visning.length === 1 ? visning[0] : "all";

  const exportExcel = async () => {
    try {
      const r = await api.get("/le/goals/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "kvalitets-og-miljoemaal.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Mål eksporteret");
    } catch (e) { toast.error("Kunne ikke eksportere"); }
  };

  const categories = useMemo(() => sv.opt("kategori").map((o) => o.value).filter((c) => c !== "Uden kategori"), [sv.data]); // eslint-disable-line
  const counts = sv.data?.counts || { open: 0, overdue: 0, closed: 0 };
  const shown = goals;
  const filters = activeFilters(sv, { status: "Status", kategori: "Kategori", ansvarlig: "Ansvarlig" });
  filters.unshift(["Visning", curView && curView !== "all" ? (({ open: 'Aktive', overdue: 'Overskredne', closed: 'Lukkede' })[curView] || curView) : "Alle"]);
  const groups = useMemo(() => {
    const map = {};
    shown.forEach((g) => { const k = g.category || "Uden kategori"; (map[k] = map[k] || []).push(g); });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0], "da"));
  }, [shown]);

  return (
    <div className="space-y-6" data-testid="le-goals-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps flex items-center gap-2 text-[#806B61]"><Target className="h-3.5 w-3.5" /> Ledelse · Ledelsesevaluering</p>
          <h1 className="text-4xl font-semibold text-[#1A1919] mt-1">Kvalitets- & miljømål</h1>
          <p className="text-stone-500 mt-2">Levende mål med baseline, handlingsplan, ansvarlige og løbende evaluering.</p>
        </div>
        <div className="flex items-center gap-2">
          <ListActions onExport={sv.exportXlsx} more={[]}
            primary={<Button data-testid="le-new-goal-btn" onClick={() => setCreateOpen(true)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Plus className="h-4 w-4 mr-1.5" /> Nyt mål</Button>}>
            <ArchiveButton kind="le_goals" onRestored={load} /></ListActions>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-lg">
        <Stat label="Aktive" value={counts.open} tone="text-[#806B61]" />
        <Stat label="Overskredne" value={counts.overdue} tone="text-[#A83232]" />
        <Stat label="Lukkede" value={counts.closed} tone="text-[#5f6b4a]" />
      </div>

      <QrFilterBar search={sv.search} setSearch={sv.onSearch} hideQuick hideSaved placeholder="Søg mål, nr., kategori, ansvarlig…" shown={shown.length} total={sv.data?.total ?? 0} onReset={sv.reset} leadActive={curView !== "open"} match={sv.match} setMatch={sv.setMatch}
        filters={[sv.mkFilter("status", "Status"), sv.mkFilter("kategori", "Kategori"), sv.mkFilter("ansvarlig", "Ansvarlig")]} period={sv.period}
        lead={<div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5 h-9 items-center">{FILTERS.map(([v, l]) => (
          <button key={v} type="button" data-testid={`le-goals-filter-${v}`} onClick={() => setView(v)} className={`px-3 h-full rounded-[10px] text-sm font-medium ${curView === v ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{l}</button>))}</div>} />
      <ListPrint kicker="BSM A/S · Ledelsesevaluering" title="Kvalitets- & miljømål" noun="mål" filters={filters}
        kpis={[{ label: "Mål i udvalget", value: shown.length }, { label: "Overskredne", value: counts.overdue, dark: true }, { label: "Aktive", value: counts.open }, { label: "Lukkede", value: counts.closed }]}
        summary={<PSummary>{pl(shown.length, "mål", "mål")} i udvalget, heraf {pl(counts.overdue, "overskredet", "overskredne")} og {pl(counts.closed, "lukket", "lukkede")}.</PSummary>}
        cols={[{ label: "Mål", key: "goal_no", w: "26mm", cls: "nowrap" }, { label: "Kategori", key: "category", w: "30mm" }, { label: "Titel", key: "title" }, { label: "Ansvarlig", key: "resp2", w: "34mm" }, { label: "Frist", key: "frist", w: "26mm", cls: "nowrap" }, { label: "Status", key: "status", w: "24mm", cls: "nowrap" }]}
        rows={shown.map((g) => ({ ...g, frist: daDate(g.deadline), resp2: clamp2(g.responsible_name), title: clamp2(g.title) }))} />

      {loading ? <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div> : shown.length === 0 ? (
        <Card className="p-10 text-center" data-testid="le-goals-empty">
          <Target className="h-9 w-9 mx-auto text-stone-300" strokeWidth={1.5} />
          <p className="mt-3 text-[#1A1919] font-medium">Ingen mål matcher filtrene</p>
          <p className="text-sm text-stone-500 mt-1">Opret et nyt mål for at komme i gang.</p>
        </Card>
      ) : (
        <div className="space-y-5" data-testid="goals-cards">
          {groups.map(([category, list]) => (
            <div key={category} className="space-y-3" data-testid={`le-goal-group-${category}`}>
              <p className="label-caps text-[#806B61] px-1">{category} <span className="text-stone-300">· {list.length}</span></p>
              {list.map((g) => full[g.id] ? <GoalCard key={g.id} goal={full[g.id]} actions={(acts || []).filter((a) => a.goal_id === g.id)} /> : <GoalRow key={g.id} g={g} nav={nav} />)}
            </div>
          ))}
        </div>
      )}

      {createOpen && <GoalFormDialog open={createOpen} onOpenChange={setCreateOpen} users={users} categories={categories} onSaved={load} />}
    </div>
  );
}
